import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync } from 'fs';
import { join } from 'path';
import { clearCredentials, getApiKey, loadConfig } from '../lib/config.js';
import { validateApiKey } from '../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { MutagentError } from '../lib/errors.js';
import { MutationContext } from '../lib/mutation-context.js';
import { runPostOnboarding } from './onboarding.js';
import { performLoginAction, buildLoginJsonResponse } from '../lib/auth-flow.js';

interface LoginOptions {
  endpoint: string;
  browser?: boolean;
  nonInteractive?: boolean;
}

export function createAuthCommand(): Command {
  const auth = new Command('auth')
    .description('Authenticate with MutagenT platform')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent auth login
  ${chalk.dim('$')} mutagent auth login --browser
  ${chalk.dim('$')} mutagent auth status
  ${chalk.dim('$')} mutagent auth logout
    `);

  // Login command — back-compat alias for `mutagent login`
  // Both delegate to lib/auth-flow.ts performLoginAction.
  // See .memory/design-principles.md → "Login Unification".
  auth
    .command('login')
    .description('Authenticate and store API key')
    .option('--browser', 'Force browser-based authentication')
    .option('--non-interactive', 'Disable interactive prompts (auto-selects browser auth)')
    .option('--endpoint <url>', 'API endpoint', 'https://api.mutagent.io')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent auth login                    ${chalk.dim('# Interactive (choose method)')}
  ${chalk.dim('$')} mutagent auth login --browser          ${chalk.dim('# Browser OAuth flow')}
  ${chalk.dim('$')} mutagent auth login --non-interactive  ${chalk.dim('# Auto browser flow (AI agents)')}

${chalk.dim('Note: this command is an alias for `mutagent login`.')}
${chalk.dim('See `mutagent login --help` for full documentation.')}
    `)
    .action(async (options: LoginOptions) => {
      const isJson = getJsonFlag(auth);
      const output = new OutputFormatter(isJson ? 'json' : 'table');
      try {
        const result = await performLoginAction({
          endpoint: options.endpoint,
          browser: options.browser,
          nonInteractive: options.nonInteractive,
          isJson,
          output,
        });

        if (isJson) {
          output.output(buildLoginJsonResponse(result));
        } else {
          output.success('Authenticated successfully');
          if (result.organization?.name) output.info(`Organization: ${result.organization.name}`);
          if (result.workspace?.name) output.info(`Workspace: ${result.workspace.name}`);
          output.info(`Endpoint: ${result.endpoint}`);
          output.info('Next: mutagent workspaces list --json');
        }

        // Post-onboarding wizard: only on first interactive login
        if (result.wasFirstLogin && process.stdin.isTTY && !isJson) {
          await runPostOnboarding();
        }
      } catch (error) {
        if (error instanceof MutagentError) {
          output.error(error.message);
          process.exit(error.exitCode);
        }
        throw error;
      }
    });


  // Status command
  auth
    .command('status')
    .description('Check authentication status')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent auth status
  ${chalk.dim('$')} mutagent auth status --json
    `)
    .action(async () => {
      const isJson = getJsonFlag(auth);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      const apiKey = getApiKey();
      const config = loadConfig();
      const endpoint = config.endpoint ?? 'https://api.mutagent.io';

      if (!apiKey) {
        if (isJson) {
          output.output({ authenticated: false, message: 'Not authenticated' });
        } else {
          output.error('Not authenticated');
          output.info('Run: mutagent auth login');
        }
        process.exit(0);
      }

      // Validate the key
      const isValid = await validateApiKey(apiKey, endpoint);

      // Check onboarding and context state
      const cwd = process.cwd();
      const hasOnboarding = existsSync(join(cwd, '.mutagentrc.json'));
      const hasContextFile = existsSync(join(cwd, '.mutagent', 'mutation-context.md'));

      let contextSummary: { prompts: number; datasets: number; evaluations: number; integrated: string[] } | undefined;
      if (hasContextFile) {
        try {
          const ctx = MutationContext.load(cwd);
          contextSummary = ctx.getSummary();
        } catch {
          // Silently ignore parse errors
        }
      }

      if (isJson) {
        const statusResult: Record<string, unknown> = {
          authenticated: isValid,
          endpoint,
          keyPrefix: `${apiKey.slice(0, 8)}...`,
          defaultWorkspace: config.defaultWorkspace,
          defaultOrganization: config.defaultOrganization,
          onboarding: hasOnboarding,
          contextFile: hasContextFile,
          context: contextSummary ?? null,
        };
        if (!isValid) {
          statusResult.message = 'Authentication invalid — run `mutagent auth login --browser`';
          statusResult.remediation = {
            interactive: 'mutagent auth login --browser',
            nonInteractive: 'export MUTAGENT_API_KEY=<your-key>',
            ciCd: 'export MUTAGENT_API_KEY=<key>',
          };
        }
        output.output(statusResult);
      } else {
        if (isValid) {
          output.success('Authentication: Authenticated');
          output.info(`Endpoint: ${endpoint}`);
          output.info(`Key: ${apiKey.slice(0, 8)}...`);
          if (config.defaultWorkspace) {
            output.info(`Workspace: ${config.defaultWorkspace}`);
          }
          if (config.defaultOrganization) {
            output.info(`Organization: ${config.defaultOrganization}`);
          }
        } else {
          output.error('Authentication: Invalid — run `mutagent auth login --browser`');
          console.error('');
          console.error('Authentication required. Options:');
          console.error('  Interactive:     mutagent auth login --browser');
          console.error('  Non-interactive: export MUTAGENT_API_KEY=<your-key>');
          console.error('  CI/CD:           export MUTAGENT_API_KEY=<key>');
        }

        // Onboarding status
        if (hasOnboarding) {
          output.success('Onboarding: Complete (.mutagentrc.json found)');
        } else {
          output.warn('Onboarding: Not initialized (run `mutagent init`)');
        }

        // Context file status
        if (hasContextFile && contextSummary) {
          const parts = [
            `${String(contextSummary.prompts)} prompts`,
            `${String(contextSummary.datasets)} datasets`,
            `${String(contextSummary.evaluations)} evaluations`,
          ];
          output.success(`Context File: .mutagent/mutation-context.md (${parts.join(', ')})`);
        } else if (hasContextFile) {
          output.success('Context File: .mutagent/mutation-context.md');
        } else {
          output.warn('Context File: Not found (run `mutagent explore`)');
        }

        // Integration status
        if (contextSummary && contextSummary.integrated.length > 0) {
          output.success(`Integration: ${contextSummary.integrated.join(', ')} (active)`);
        }
      }

      if (!isValid) {
        process.exit(1);
      }
    });

  // Logout command
  auth
    .command('logout')
    .description('Clear stored credentials')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent auth logout
    `)
    .action(() => {
      const isJson = getJsonFlag(auth);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      clearCredentials();
      output.success('Logged out successfully');
    });

  return auth;
}
